/**
 * Data model for the single-entity dashboard's "Gross Revenue (monthly)"
 * chart (dashboard redesign step 3; rework of the former "Income, Expenses
 * & Net Income" chart), same shape as the Overall dashboard's group-level
 * chart (lib/groupGrossRevenueChart.js) so the two read as one system.
 *
 * Every figure is composed from an existing canonical source, never
 * re-derived:
 *   - Revenue, costs, net result: GET /reports/entity-pnl "months" array
 *     (services/entity_pnl.get_entity_pnl), routed per entity to BR OS or
 *     Property Portfolio via PNL_SOURCE_REGISTRY -- never both for the same
 *     entity, so exactly one of the two segments is ever nonzero.
 *   - Gross margin: BR OS = net_result + invoice_financing + entity_opex
 *     (i.e. revenue - cogs, the same "above cogs" definition
 *     routes/bros_dashboard.py uses for total_gross_margin); Portfolio =
 *     net_result + financing (the same convention lib/portfolioDerived.js
 *     already uses for the group chart). Net margin is net_result directly.
 *   - Forecast (Property Portfolio entities only -- BR OS has no
 *     forward-booking source): GET /reports/forecast?scope=entity&id=,
 *     the "monthly" array, same forward-booking floor the group chart and
 *     the Property Portfolio dashboard use. Its gross_revenue_gbp field is
 *     always GBP-denominated regardless of the entity's own currency, so
 *     for a EUR-native entity (e.g. a French SCI) it must be converted to
 *     match entityPnl.currency before joining the EUR-denominated actuals
 *     -- otherwise the forecast bar would show a raw GBP number mislabeled
 *     as EUR, exactly the cross-currency-without-conversion bug class this
 *     codebase treats as critical. input.entityCurrency + input.fxRates
 *     (the same rates map bankAccountsAPI.getSummary already returns, used
 *     via lib/currencyConvert.js's convertAmount) drive that conversion;
 *     GBP-native entities pass through unchanged (rate 1).
 *
 * The BR OS / Portfolio segment keys, order and colors intentionally match
 * groupGrossRevenueChart.js's GROSS_REVENUE_SEGMENTS (BR OS bottom,
 * Portfolio top) so the two charts are visually identical in stacking
 * order; duplicated here as a tiny local constant rather than imported
 * cross-module, since these two segments are effectively fixed (a third
 * PNL_SOURCE_REGISTRY value would need a product decision either way).
 */

import { getActualsForecastBoundary } from './forecastChartUtils';
import { annotateForecastTotals } from './groupGrossRevenueChart';
import { convertAmount } from './currencyConvert';

export var ENTITY_GROSS_REVENUE_SEGMENTS = [
  { key: 'br_os', label: 'BR OS', color: '#3B82F6' },
  { key: 'property_portfolio', label: 'Portfolio', color: '#10B981' },
];

function byMonthKey(list) {
  var m = {};
  (list || []).forEach(function (row) { if (row && row.month) m[row.month] = row; });
  return m;
}

/**
 * @param {object} input
 * @param {number} input.year
 * @param {string} input.source - entityPnl.source: 'bros' | 'portfolio' | 'none' | 'business'
 *   (get_business_pnl's merged "All Entities" aggregate). Not branched on below:
 *   a single-source entity's months only ever carry that source's fields (the
 *   other side's fields are simply absent, contributing zero), and a merged
 *   'business' month carries BOTH sides' fields at once -- one unconditional
 *   formula is correct for all cases.
 * @param {object[]} input.months - entityPnl.months
 * @param {object[]} [input.forecastMonthly] - /reports/forecast (scope=entity) monthly array
 * @param {string} [input.entityCurrency] - entityPnl.currency; the forecast's native GBP is converted to this
 * @param {object} [input.fxRates] - live rates map (e.g. bankAccountsAPI.getSummary's fx_rates), for the GBP->entityCurrency conversion
 * @param {string[]} input.monthLabels - short month labels, e.g. ['Jan', ..., 'Dec']
 * @param {Date} [input.refDate] - injectable "today" for the actuals/forecast boundary (tests)
 */

/**
 * BR OS forward overlay, shared by the revenue and margin builders.
 *
 * Superseded rule (2026-08-30): "BR OS has no forward-booking source at
 * all". It does now -- services/bros_forward_revenue.py recognises the
 * nights of already-contracted stays, and /reports/bros/dashboard returns
 * them as forecast_months. All BR OS revenue belongs to ONE entity
 * (config.PNL_SOURCE_REGISTRY maps only br_ltd to 'bros'), so applying the
 * BR OS-wide forward series to a 'bros' entity or to the merged 'business'
 * aggregate counts it exactly once.
 *
 * `field` is 'turnover' (booking volume) or 'gross_margin' (margin/net
 * revenue). Amounts arrive in GBP and are converted to the entity currency,
 * same convention as the Portfolio forecast.
 */
function overlayBrosForecast(rows, input, boundary, field) {
  var source = input.source;
  if (source !== 'bros' && source !== 'business') return;
  var byMonth = byMonthKey(input.brosForecastMonths);
  var entityCurrency = input.entityCurrency || 'GBP';
  var fxRates = input.fxRates || {};
  rows.forEach(function (row) {
    if (row._monthKey < boundary.firstForecastMonthKey || row._monthKey > boundary.lastForecastMonthKey) return;
    // No "actual is nonzero" guard: the BR OS forecast never overlaps its
    // actuals (uninvoiced share of live stays + invoices due later in the
    // window), so the current month shows actual + remainder (2026-10-06).
    var fc = byMonth[row._monthKey];
    if (!fc) return;
    row['BR OS_forecast'] = convertAmount(parseFloat(fc[field] || 0), 'GBP', entityCurrency, fxRates) || 0;
    row._isForecast = true;
  });
}

export function buildEntityGrossRevenueChartData(input) {
  var year = input.year;
  var source = input.source;
  var byMonth = byMonthKey(input.months);
  var forecastByMonth = byMonthKey(input.forecastMonthly);
  var entityCurrency = input.entityCurrency || 'GBP';
  var fxRates = input.fxRates || {};
  var monthLabels = input.monthLabels;
  var boundary = getActualsForecastBoundary(input.refDate);

  var rows = [];
  for (var i = 0; i < 12; i++) {
    var mk = year + '-' + String(i + 1).padStart(2, '0');
    var m = byMonth[mk] || {};
    var netResult = parseFloat(m.net_result) || 0;

    // BR OS `revenue` is actual + forward (services/entity_pnl.py); the bar
    // is the actual part, the forward part is the overlay below.
    var brOsRevenue = parseFloat(
      m.bros_revenue_actual != null ? m.bros_revenue_actual
        : (m.revenue_actual != null ? m.revenue_actual : m.revenue)
    ) || 0;
    // Actuals/forecast split (services/entity_pnl.py): gross_revenue_actual
    // is check_in < today only. A booking dated in the future is never an
    // actual, regardless of status -- fall back to the unsplit total only
    // when the backend/fixture hasn't provided the split fields at all.
    var portfolioRevenue = (m.gross_revenue_actual != null || m.gross_revenue_forward != null)
      ? (parseFloat(m.gross_revenue_actual) || 0)
      : (parseFloat(m.gross_revenue) || 0);
    // Margin actuals/forecast split (dashboards round 2 follow-up): same
    // standing rule as revenue above -- a future-dated booking's margin
    // contribution is exactly as un-incurred as its revenue. net_result_actual/
    // gross_margin_actual (services/entity_pnl.py) are read directly rather
    // than re-derived; gross_margin_actual already folds in BR OS's full
    // invoice_financing/entity_opex for a merged 'business' month, so when
    // it's present it's used as-is, never added to a second time. Falls back
    // to the unsplit total (BR OS's own formula, always fully actual) only
    // when the backend/fixture hasn't provided the split.
    var netMarginActual = m.net_result_actual != null ? (parseFloat(m.net_result_actual) || 0) : netResult;
    var grossMarginActual = m.gross_margin_actual != null
      ? (parseFloat(m.gross_margin_actual) || 0)
      : (netMarginActual
        + (parseFloat(m.invoice_financing) || 0)
        + (parseFloat(m.entity_opex) || 0)
        + (parseFloat(m.financing) || 0));

    var row = {
      month: monthLabels[i],
      _monthKey: mk,
      'BR OS': brOsRevenue,
      Portfolio: portfolioRevenue,
      _grossMargin: grossMarginActual,
      _netMargin: netMarginActual,
    };
    row._total = row['BR OS'] + row.Portfolio;
    row._carrier = row.Portfolio > 0 ? 'property_portfolio' : (row['BR OS'] > 0 ? 'br_os' : null);
    // A margin LINE has no value in a future month; 0 drew a false cliff.
    if (row._monthKey > boundary.currentMonthKey) {
      if ('_grossMargin' in row) row._grossMargin = null;
      if ('_netMargin' in row) row._netMargin = null;
    }
    rows.push(row);
  }

  // Forecast overlay: Property Portfolio segment only. BR OS has no
  // forward-booking source at all (a domain fact, not just a data-shape
  // quirk of today's fixtures) so it's explicitly excluded here rather than
  // relying on its fields being absent -- a stray forecastMonthly entry must
  // never paint a forecast bar onto a pure BR OS entity's chart. 'business'
  // (get_business_pnl's merged aggregate) and 'portfolio' both carry real
  // portfolio contribution and go through this loop. Months before the
  // current month are pure actuals (untouched). The current month and every
  // month after it get a forecast-styled remainder: for the current month
  // that's the rest of the month (gross_revenue_forward); for a later month
  // it's the whole month, and its actuals bar is forced to zero regardless
  // of what the backend computed (never let a future check-in render as an
  // actual).
  if (source !== 'bros') rows.forEach(function (row) {
    if (row._monthKey < boundary.currentMonthKey) return; // past: pure actuals
    if (row._monthKey > boundary.lastForecastMonthKey) return; // 3-month window

    var m = byMonth[row._monthKey] || {};
    var isFuture = row._monthKey > boundary.currentMonthKey;
    var forward = (m.gross_revenue_actual != null || m.gross_revenue_forward != null)
      ? (parseFloat(m.gross_revenue_forward) || 0)
      : null;

    if (forward == null) {
      // Backend hasn't provided the split (older data / test fixture, or a
      // bros-only/holding entity with no portfolio contribution at all):
      // fall back to the separate rolling forward-booking endpoint, within
      // its window only, and never stacked on top of a nonzero actual.
      if (row._monthKey < boundary.firstForecastMonthKey || row._monthKey > boundary.lastForecastMonthKey) return;
      var fc = forecastByMonth[row._monthKey];
      if (!fc) return;
      if (row.Portfolio > 0) return;
      row.Portfolio_forecast = convertAmount(fc.gross_revenue_gbp || 0, 'GBP', entityCurrency, fxRates) || 0;
      row._isForecast = true;
      return;
    }

    if (isFuture) {
      row.Portfolio = 0; // a future booking is never an actual, regardless of status
      row._total = row['BR OS'] + row.Portfolio;
    }
    row.Portfolio_forecast = forward;
    row._isForecast = true;
  });

  overlayBrosForecast(rows, input, boundary, 'turnover');

  return annotateForecastTotals(rows, ENTITY_GROSS_REVENUE_SEGMENTS.map(function (seg) { return seg.label; }));
}

/**
 * Data model for the entity/aggregate dashboards' "Margin" chart (companion
 * to buildEntityGrossRevenueChartData above, now that the Gross Revenue
 * chart no longer carries margin lines). Same BR OS/Portfolio segment split,
 * same sources, same single-source-degrades-to-one-segment rule -- just
 * returns Net Margin per segment (for stacked bars) instead of a single
 * combined figure.
 *
 * For the 'business' aggregate (get_business_pnl), the combined
 * net_result_actual/gross_margin_actual fields can't be un-summed, so this
 * reads the separate bros_net_margin_actual/portfolio_net_margin_actual
 * (and gross_ prefixed) fields the backend now also provides. For a
 * single-source month (a single entity, or the 'personal'/'portfolio'
 * scopes, which are always source='portfolio'), those split fields are
 * absent and the entity's one combined actual figure is attributed in full
 * to whichever segment is its actual source -- exactly one is ever nonzero,
 * mirroring buildEntityGrossRevenueChartData's own degrade rule.
 *
 * Forecast: only the 3-months-ahead window (never a current-month partial),
 * matching the reference Property Portfolio Margin chart and
 * buildGroupMarginChartData -- BR OS never gets a forecast bar.
 *
 * @param {object} input - same shape as buildEntityGrossRevenueChartData's input
 */
export function buildEntityMarginChartData(input) {
  var year = input.year;
  var source = input.source;
  var byMonth = byMonthKey(input.months);
  var forecastByMonth = byMonthKey(input.forecastMonthly);
  var entityCurrency = input.entityCurrency || 'GBP';
  var fxRates = input.fxRates || {};
  var monthLabels = input.monthLabels;
  var boundary = getActualsForecastBoundary(input.refDate);

  var rows = [];
  for (var i = 0; i < 12; i++) {
    var mk = year + '-' + String(i + 1).padStart(2, '0');
    var m = byMonth[mk] || {};

    var brOsNetMargin, portfolioNetMargin, brOsGrossMargin, portfolioGrossMargin;
    if (source === 'business') {
      // 'business' aggregate: the backend provides the split directly.
      brOsNetMargin = parseFloat(m.bros_net_margin_actual) || 0;
      portfolioNetMargin = parseFloat(m.portfolio_net_margin_actual) || 0;
      brOsGrossMargin = parseFloat(m.bros_gross_margin_actual) || 0;
      portfolioGrossMargin = parseFloat(m.portfolio_gross_margin_actual) || 0;
    } else {
      // Single-source month: same fallback formula
      // buildEntityGrossRevenueChartData uses for its combined figure,
      // attributed in full to whichever side is this entity's actual
      // source ('bros' or 'portfolio'; 'none' contributes zero either way).
      var netResult = parseFloat(m.net_result) || 0;
      var netMarginActual = m.net_result_actual != null ? (parseFloat(m.net_result_actual) || 0) : netResult;
      var grossMarginActual = m.gross_margin_actual != null
        ? (parseFloat(m.gross_margin_actual) || 0)
        : (netMarginActual
          + (parseFloat(m.invoice_financing) || 0)
          + (parseFloat(m.entity_opex) || 0)
          + (parseFloat(m.financing) || 0));
      brOsNetMargin = source === 'bros' ? netMarginActual : 0;
      portfolioNetMargin = source === 'bros' ? 0 : netMarginActual;
      brOsGrossMargin = source === 'bros' ? grossMarginActual : 0;
      portfolioGrossMargin = source === 'bros' ? 0 : grossMarginActual;
    }

    var row = {
      month: monthLabels[i],
      _monthKey: mk,
      'BR OS': brOsNetMargin,
      Portfolio: portfolioNetMargin,
      _grossMargin: brOsGrossMargin + portfolioGrossMargin,
    };
    row._total = row['BR OS'] + row.Portfolio;
    // A margin LINE has no value in a future month; 0 drew a false cliff.
    if (row._monthKey > boundary.currentMonthKey) {
      if ('_grossMargin' in row) row._grossMargin = null;
      if ('_netMargin' in row) row._netMargin = null;
    }
    rows.push(row);
  }

  // Forecast overlay: Portfolio segment only, 3-months-ahead window. BR OS
  // has no forward-booking source, same standing rule as the revenue chart.
  // Guards against stacking a forecast bar on top of a nonzero actual (same
  // defensive rule buildEntityGrossRevenueChartData's own forecast-fallback
  // branch uses), never double-counting margin for a month the actuals
  // pipeline unexpectedly populated.
  if (source !== 'bros') rows.forEach(function (row) {
    if (row._monthKey < boundary.firstForecastMonthKey || row._monthKey > boundary.lastForecastMonthKey) return;
    if (row._monthKey === boundary.currentMonthKey) {
      // Current month: Portfolio remainder = forward-dated bookings' margin.
      var cm = byMonth[row._monthKey] || {};
      var fwd = cm.net_result_forward != null ? (parseFloat(cm.net_result_forward) || 0) : 0;
      if (source === 'business') {
        // Merged forward = BR OS forward + Portfolio forward; take BR OS out
        // (it is drawn from the BR OS forecast overlay).
        fwd -= (parseFloat(cm.revenue_forward) || 0) - (parseFloat(cm.cogs_forward) || 0);
      }
      if (fwd !== 0) {
        row.Portfolio_forecast = fwd;
        row._isForecast = true;
      }
      return;
    }
    if (row.Portfolio > 0) return;
    var fc = forecastByMonth[row._monthKey];
    if (!fc) return;
    row.Portfolio_forecast = convertAmount(fc.margin_gbp || 0, 'GBP', entityCurrency, fxRates) || 0;
    row._isForecast = true;
  });

  overlayBrosForecast(rows, input, boundary, 'gross_margin');

  return annotateForecastTotals(rows, ENTITY_GROSS_REVENUE_SEGMENTS.map(function (seg) { return seg.label; }));
}

/**
 * YTD (actual months only) per-segment sums plus the combined total, for the
 * figure line under the chart. Forecast-only months contribute zero to both
 * (their actuals fields are 0 by construction).
 */
export function computeEntityGrossRevenueYtd(chartData) {
  var out = { _combined: 0 };
  ENTITY_GROSS_REVENUE_SEGMENTS.forEach(function (seg) { out[seg.label] = 0; });
  (chartData || []).forEach(function (row) {
    ENTITY_GROSS_REVENUE_SEGMENTS.forEach(function (seg) {
      out[seg.label] += row[seg.label] || 0;
    });
    out._combined += row._total || 0;
  });
  return out;
}
